import { Injectable } from '@angular/core';
import { IEngineEventListener, IMouseEngineEvent, IReaderView, IReaderViewAnnotation, IReaderViewAnnotationEngineEvent, ISelectionChangedEngineEvent } from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import { EpubHighlightService } from './epub-highlight.service';
import { IMarkerColor, IMarkerContent } from 'src/app/core/interfaces';
import { resetEpubCursorIcon, resetEpubSelectionColor, setEpubSelectionColor, updateEpubCursorIcon } from 'src/app/core/common';
import { CustomEpubCursorStyles, EpubAnnotationMode } from 'src/app/core/epub-utils/enums';
import { EpubBookmarkService, IEpubBookmarkData } from './epub-bookmark.service';
import { CurrentUiStoreService } from '../ui/current-ui-store.service';
import { CurrentUIState } from 'src/models/UIState';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';
import { DocumentManagerService } from '../document/document-manager.service';
import { AsgardDocEpub } from 'src/app/core/documents/asgard-doc-epub';
import { AsgardDocBase } from 'src/app/core/documents/asgard-doc-base';
import { BehaviorSubject } from 'rxjs';
import { EpubCommentService, IEpubCommentData } from './epub-comment.service';

@Injectable({
    providedIn: 'root'
})
export class EpubAnnotationService {
    public lastSelectionChangedEvent$: BehaviorSubject<ISelectionChangedEngineEvent | undefined> = new BehaviorSubject(undefined);
    public lastClickEvent$: BehaviorSubject<IMouseEngineEvent | undefined> = new BehaviorSubject(undefined);
    public activeMode: EpubAnnotationMode = EpubAnnotationMode.None;

    constructor(
        private readonly highlightService: EpubHighlightService,
        private readonly bookmarkService: EpubBookmarkService,
        private readonly commentService: EpubCommentService,
        private readonly documentManagerService: DocumentManagerService,
        private readonly uiService: CurrentUiStoreService
    ) {
        this.uiService.getUIState().subscribe((e: CurrentUIState) => {
            if (!this.readerView) return;

            if (e[AsgardFeatureNames.TEXT_NOTES].active) {
                this.setAnnotationMode(EpubAnnotationMode.Textnote);
            }
        });
    }

    private get readerView(): IReaderView | undefined {
        const currDoc = this.documentManagerService.currentDoc;
        if (!this.isEpubDocAndReady(currDoc)) return undefined;

        return (currDoc as AsgardDocEpub).epubViewerInstance?.readerView;
    }

    private get docId(): string | undefined {
        return this.documentManagerService.currentDoc?.documentId;
    }

    private handleClickListener: IEngineEventListener<IMouseEngineEvent> = (event) => {
        this.lastClickEvent$.next(event);
        if(this.activeMode === EpubAnnotationMode.Marker) {
            event.readerView.clearContentSelection();
        }
        this.handleLastSelectionEvent();
    }

    private handleContentSelectionListener: IEngineEventListener<ISelectionChangedEngineEvent> = (event) => {
        this.lastSelectionChangedEvent$.next(event);
    }

    private openBookmarkClickListener: IEngineEventListener<IMouseEngineEvent> = (event) => {
        this.bookmarkService.createOrOpenBookmark(event);
        this.setAnnotationMode(EpubAnnotationMode.None);
        this.uiService.setItemUnActive(AsgardFeatureNames.TEXT_NOTES);
    }

    public initAnnotationLayers(docId: string) {
        const readerView = (this.documentManagerService.getDocumentById(docId) as AsgardDocEpub).epubViewerInstance?.readerView;
        if (!readerView) return;

        this.addHighlightsLayer(readerView);
        this.addBookmarksLayer(readerView);
        this.addCommentsLayer(readerView);

        readerView?.addEngineEventListener('selectionChanged', this.handleContentSelectionListener);
        readerView?.addEngineEventListener('click', this.handleClickListener);
    }

    public destroyAnnotationLayers(docId: string) {
        const doc = this.documentManagerService.getDocumentById(docId);
        if (!this.isEpubDocAndReady(doc)) return;
        const readerView = (doc as AsgardDocEpub).epubViewerInstance?.readerView;
        if (readerView) {
            readerView.getAnnotationLayers().forEach(al => this.readerView?.destroyAnnotationLayer(al));
            readerView.removeEngineEventListener('selectionChanged', this.handleContentSelectionListener)
        }
        this.resetState();
    }

    public setAnnotationMode(mode: EpubAnnotationMode) {
        if (!this.readerView) return;

        this.readerView?.removeEngineEventListener('click', this.handleClickListener);
        this.readerView?.removeEngineEventListener('click', this.openBookmarkClickListener)
        this.activeMode = mode;

        if (this.activeMode === EpubAnnotationMode.None) {
            resetEpubCursorIcon();
            resetEpubSelectionColor();
            this.readerView?.addEngineEventListener('click', this.handleClickListener);
        } else if (this.activeMode === EpubAnnotationMode.Textnote) {
            this.readerView?.addEngineEventListener('click', this.openBookmarkClickListener);
            updateEpubCursorIcon(CustomEpubCursorStyles.textnote);
        } else {
            this.readerView?.addEngineEventListener('click', this.handleClickListener);
            if (mode === EpubAnnotationMode.Eraser) {
                updateEpubCursorIcon(CustomEpubCursorStyles.erase, true);
                resetEpubSelectionColor();
            }
        }
    }

    public setHighlightColor(color: IMarkerColor) {
        if (!this.docId) return;
        this.highlightService.setColor(color);
        updateEpubCursorIcon(CustomEpubCursorStyles[color.color], true);
        setEpubSelectionColor(color.hex);
        updateEpubCursorIcon(CustomEpubCursorStyles[color.color]);
        setEpubSelectionColor(color.hex);
    }

    public async getMarkers(): Promise<IMarkerContent[]> {
        return this.highlightService.getMarkers();
    }

    public getComments(): IReaderViewAnnotation<IEpubCommentData>[] {
        return this.commentService.getComments();
    }

    public getBookmarks(): IReaderViewAnnotation<IEpubBookmarkData>[] {
        return this.bookmarkService.getBookmarks();
    }

    public deleteCommentById(id: number) {
        return this.commentService.deleteCommentById(id);
    }

    public deleteBookmarkById(id: number) {
        return this.bookmarkService.deleteBookmarkById(id);
    }

    public createBookmarkForSelectedText() {
        if (!this.lastSelectionChangedEvent$.value || !this.lastSelectionChangedEvent$.value.contentLocation) return;

        this.bookmarkService.createBookmark(this.lastSelectionChangedEvent$.value.contentLocation);
        this.lastSelectionChangedEvent$.next(undefined);
    }

    public addCommentToSelectedText() {
        if (!this.lastSelectionChangedEvent$.value || !this.lastSelectionChangedEvent$.value.contentLocation) return;

        this.commentService.openNewCommentEditor(this.lastSelectionChangedEvent$.value.contentLocation);
        this.lastSelectionChangedEvent$.next(undefined);
    }

    private handleLastSelectionEvent() {
        if (!this.lastSelectionChangedEvent$.value || !this.lastSelectionChangedEvent$.value.isRange) return;

        switch (this.activeMode) {
            case EpubAnnotationMode.Marker:
                this.highlightService.createHighlight(this.lastSelectionChangedEvent$.value);
                break;
            case EpubAnnotationMode.Eraser:
                this.highlightService.eraseHighlight(this.lastSelectionChangedEvent$.value);
                break;
            case EpubAnnotationMode.None:
                return;
        }
        this.lastSelectionChangedEvent$.next(undefined);
    }

    private resetState(): void {
        this.activeMode = EpubAnnotationMode.None;
        this.lastSelectionChangedEvent$.next(undefined);
    }

    private addHighlightsLayer(readerView: IReaderView): void {
        const highlightsLayer = readerView.createAnnotationLayer(this.highlightService.LAYER_NAME);
        highlightsLayer.setLayerOptions({
            layerStyle: { 'mix-blend-mode': 'multiply' }
        });
    }

    private addBookmarksLayer(readerView: IReaderView): void {
        const bookmarkLayer = readerView.createAnnotationLayer(this.bookmarkService.LAYER_NAME);
        bookmarkLayer.setDefaultAnnotationOptions({ containerClassName: 'epub-bookmarks-container' });
        bookmarkLayer.setLayerOptions({
            annotationInputEngineEventsEnabled: true,
        });

        bookmarkLayer.addEngineEventListener('annotationClick', (event: IReaderViewAnnotationEngineEvent) => {
            this.bookmarkService.showBookmarkEditor(event.annotation);
        });
        bookmarkLayer.addEngineEventListener('annotationIntersectingVisibleRange', (event: IReaderViewAnnotationEngineEvent) => {
            this.bookmarkService.attachBookmarkIcon(event.annotation);
        });
    }

    private addCommentsLayer(readerView: IReaderView): void {
        const commentsLayer = readerView.createAnnotationLayer(this.commentService.LAYER_NAME);
        commentsLayer.setDefaultAnnotationOptions({ containerClassName: 'epub-comments-container' });
        commentsLayer.setLayerOptions({
            annotationInputEngineEventsEnabled: true,
            layerStyle: { 'mix-blend-mode': 'multiply' }
        });
         // Fired when a pointerenter event occurred on a ReaderViewAnnotation.
        commentsLayer.addEngineEventListener('annotationPointerEnter', (event: IReaderViewAnnotationEngineEvent) => {
            this.commentService.openEditor(event.annotation);
        });
        //Fired when a pointerleave event occurred on a ReaderViewAnnotation.
        commentsLayer.addEngineEventListener('annotationPointerLeave', () => {
            this.commentService.annotationPointerLeave();
        });
    }

    private isEpubDocAndReady(doc: AsgardDocBase | null | undefined): boolean {
        return !!doc && (doc instanceof AsgardDocEpub) && doc.isDocumentReady;
    }
}